import axios, { AxiosError, AxiosProgressEvent, type AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { IAxiosRetryConfig } from 'axios-retry/dist/esm';
import logger from '../libs/logger/Logger';
import { isCancelledByUserError } from '../errors/createCanceledError';
import getPilotApiService from './PilotApiService';

// We get an occasional "We encountered an internal error" 500 error from s3;
// but we also need to consider that the pilot's internet might drop.
// So that's why the retry limit is so high here.
// We will keep retrying all upload requests until the user cancels the upload.
const RETRY_LIMIT_COUNT = 700;

// Exponential backoff with jitter, capped. Replaces the previous flat 60s delay: a brief
// network glitch now retries in ~1s instead of a full minute, while a persistent outage
// backs off to at most 30s between attempts. Jitter avoids many parts retrying in sync.
const RETRY_DELAY_BASE_MS = 1000;
const RETRY_DELAY_CAP_MS = 30000;
const RETRY_DELAY_MS = (retryCount: number) => {
  const backoff = Math.min(RETRY_DELAY_BASE_MS * 2 ** (retryCount - 1), RETRY_DELAY_CAP_MS);
  const jitter = Math.random() * backoff * 0.2;
  return backoff + jitter;
};

const retryCondition = (error: AxiosError) => {
  return !isCancelledByUserError(error);
};

// Multipart part (chunk) size. Deliberately smaller than the multipart *threshold*
// (MultiplePartUploadMinFileSizeMB, applied in uploadFileToStorage.ts): the threshold decides
// whether a file uses multipart at all, while this decides how big each uploaded chunk is.
// Smaller chunks transfer inside S3's socket-idle window on weak uplinks and are cheap to
// re-send when a part stalls, without changing the multipart threshold for everyone else.
const MULTIPART_PART_SIZE_BYTES = 16 * 1024 * 1024;

interface UploadOptions {
  signal: AbortSignal;
  customerId: string;
  siteId: string;
  uploadPath: string;
  file: File;
  excludeCustomerSitePath?: boolean;
}

interface SingleFileUploadOptions extends UploadOptions {
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  onRetry?: IAxiosRetryConfig['onRetry'];
}

interface MultipartFileUploadOptions extends UploadOptions {
  onOverallUploadProgress?: (loaded: number, total: number) => void;
  onPartRetry?: (
    partIndex: number,
    retryCount: number,
    error: AxiosError,
    requestConfig: AxiosRequestConfig,
  ) => Promise<void> | void;
  onPartUploaded?: (partIndex: number, partUploadStartTimeMs: number, size: number) => Promise<void> | void;
}

class StorageService {
  private getS3Client(onRetry?: IAxiosRetryConfig['onRetry']) {
    const client = axios.create();

    const retryConfig: IAxiosRetryConfig = {
      retries: RETRY_LIMIT_COUNT,
      retryDelay: RETRY_DELAY_MS,
      retryCondition,
    };

    if (onRetry) {
      retryConfig.onRetry = onRetry;
    }

    axiosRetry(client, retryConfig);

    return client;
  }

  private buildUploadPath(
    customerId: string,
    siteId: string,
    uploadPath: string,
    excludeCustomerSitePath: boolean,
  ): string {
    return excludeCustomerSitePath ? uploadPath : `${customerId}/${siteId}/${uploadPath}`;
  }

  async upload(options: SingleFileUploadOptions) {
    const {
      signal,
      customerId,
      siteId,
      uploadPath,
      file,
      onUploadProgress,
      onRetry,
      excludeCustomerSitePath = false,
    } = options;

    const fullPath = this.buildUploadPath(customerId, siteId, uploadPath, excludeCustomerSitePath);

    const { presignedUrl } = await getPilotApiService().getPresignedUploadUrl(signal, customerId, siteId, fullPath);

    return this.getS3Client(onRetry)
      .put(presignedUrl, file, { signal, onUploadProgress })
      .then((response) => response.data);
  }

  async multiPartUpload(options: MultipartFileUploadOptions) {
    const {
      signal,
      customerId,
      siteId,
      uploadPath,
      file,
      excludeCustomerSitePath = false,
      onOverallUploadProgress,
      onPartRetry,
      onPartUploaded,
    } = options;

    const fullPath = this.buildUploadPath(customerId, siteId, uploadPath, excludeCustomerSitePath);
    const blobs = this.splitFileIntoBlobs(file);

    const { uploadId } = await getPilotApiService().initiateMultipartUpload(signal, customerId, siteId, fullPath);

    logger.log(`Multipart upload initiated, uploadId is ${uploadId}`);

    const etags = await this.uploadParts({
      blobs,
      customerId,
      siteId,
      fullPath,
      file,
      uploadId,
      signal,
      onOverallUploadProgress,
      onPartRetry,
      onPartUploaded,
    });

    const multipartCompleteStartTimeMs = Date.now();

    await getPilotApiService().completeMultipartUpload(
      signal,
      customerId,
      siteId,
      fullPath,
      uploadId,
      etags.map((etag, i) => ({ partNumber: i + 1, etag })),
    );

    const assembleSec = (Date.now() - multipartCompleteStartTimeMs) / 1000;

    return {
      assembleSec,
      partsCount: etags.length,
    };
  }

  private async uploadParts({
    blobs,
    customerId,
    siteId,
    fullPath,
    file,
    uploadId,
    signal,
    onOverallUploadProgress,
    onPartRetry,
    onPartUploaded,
  }: {
    blobs: Blob[];
    customerId: string;
    siteId: string;
    fullPath: string;
    file: File;
    uploadId: string;
    signal: AbortSignal;
    onOverallUploadProgress?: MultipartFileUploadOptions['onOverallUploadProgress'];
    onPartRetry?: MultipartFileUploadOptions['onPartRetry'];
    onPartUploaded?: MultipartFileUploadOptions['onPartUploaded'];
  }) {
    const etags: string[] = [];
    let previousPartsSize = 0;
    const totalSize = file.size;

    for (const [index, blob] of blobs.entries()) {
      const partNumber = index + 1;

      // Fetch a fresh presigned URL right before uploading each part.
      // On 403 retry (expired URL), the onRetry callback fetches another fresh one.
      let { presignedUrl } = await getPilotApiService().getMultipartPartUrl(
        signal,
        customerId,
        siteId,
        fullPath,
        uploadId,
        partNumber,
      );

      const client = this.getS3Client(async (retryCount, error, requestConfig) => {
        if ((error as AxiosError).response?.status === 403) {
          ({ presignedUrl } = await getPilotApiService().getMultipartPartUrl(
            signal,
            customerId,
            siteId,
            fullPath,
            uploadId,
            partNumber,
          ));
          requestConfig.url = presignedUrl;
        }
        onPartRetry?.(index, retryCount, error as AxiosError, requestConfig);
      });

      const partUploadStartTimeMs = Date.now();
      const response = await client.put(presignedUrl, blob, {
        signal,
        onUploadProgress: onOverallUploadProgress
          ? (progressEvent: AxiosProgressEvent) => {
              const overallLoaded = previousPartsSize + (progressEvent.loaded || 0);
              onOverallUploadProgress(overallLoaded, totalSize);
            }
          : undefined,
      });

      if (!response || response.status !== 200) {
        throw new Error(`File upload response is ${response.status}: ${response.data}`);
      }

      const etag: string = response.headers.etag;

      if (!etag) {
        throw new Error('Failed to get ETag');
      }

      onPartUploaded?.(index, partUploadStartTimeMs, blob.size);
      previousPartsSize += blob.size;
      etags.push(etag);
    }

    return etags;
  }

  private splitFileIntoBlobs(file: File): Blob[] {
    let start = 0;
    let partNum = 0;
    const blobs: Blob[] = [];

    while (start < file.size) {
      const end = Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size);
      const filePart = file.slice(start, end);

      if (filePart.size > 0) {
        blobs.push(filePart);
      }

      start = MULTIPART_PART_SIZE_BYTES * ++partNum;
    }

    return blobs;
  }
}

const storageService = new StorageService();

export const getStorageService = () => storageService;
